import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import { EstateData } from 'src/lib/fliko/models/estate/estate.data';
import { DatabaseService } from 'src/app/shared/services/database.service';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { ContextType } from './models/context.data';

interface SetEstateOptions {
  persist?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ContextService implements OnDestroy {
  private userId$ = new BehaviorSubject<string | null>(null);
  private estateId$ = new BehaviorSubject<string | null>(null);
  private companyId$ = new BehaviorSubject<string | null>(null);
  private context$ = new BehaviorSubject<ContextType | null>(null);

  private subscription$ = new Subscription();
  private estateReadCancelled$ = new Subject<void>();
  private estateChangeId = 0;
  private companyChangeId = 0;
  private requestedEstateId: string | null = null;
  private requestedCompanyId: string | null = null;
  private companyChange: Promise<void> = Promise.resolve();
  private estateChange: Promise<void> = Promise.resolve();

  constructor(
    private dbSrv: DatabaseService,
    private localStorageSrv: LocalStorageService
  ) {}

  ngOnDestroy(): void {
    ++this.estateChangeId;
    this.estateReadCancelled$.next();
    ++this.companyChangeId;
    this.subscription$.unsubscribe();
    this.estateReadCancelled$.complete();
  }

  private contextEstate = (estateId: string) => this.dbSrv.commonFactory.getObject<EstateData>(`estate/${estateId}`);

  // HACK: Friend-like declaration? https://github.com/microsoft/TypeScript/issues/7692
  public static getEstateObject = (srv: ContextService) => srv.contextEstate;

  public setUser(userId: string | null): void {
    if (this.userId$.value === userId) return;

    ++this.estateChangeId;
    this.estateReadCancelled$.next();
    ++this.companyChangeId;
    this.requestedEstateId = null;
    this.requestedCompanyId = null;
    this.companyChange = Promise.resolve();
    this.estateChange = Promise.resolve();
    this.companyId$.next(null);
    this.estateId$.next(null);
    this.context$.next(null);
    this.dbSrv.initializeWithUser(userId);
    this.userId$.next(userId);
  }

  public setEstate(estateId: string | null | undefined, options: SetEstateOptions = {}): Promise<void> {
    if (!estateId) return Promise.resolve();
    if (this.requestedEstateId === estateId) return this.estateChange;

    const changeId = ++this.estateChangeId;
    this.estateReadCancelled$.next();
    this.requestedEstateId = estateId;
    this.estateId$.next(null);
    if (options.persist !== false) this.localStorageSrv.selectedEstate.set(this.userId$.value ?? '', estateId);

    this.estateChange = Promise.all([
      this.dbSrv.changeEstate(estateId, this.estateReadCancelled$),
      this.contextEstate(estateId).snapshot(this.estateReadCancelled$)
    ])
      .then(async ([, estate]) => {
        if (changeId !== this.estateChangeId) return;
        if (!estate) {
          this.requestedEstateId = null;
          return;
        }

        await this.changeCompany(estate.companyId);
        if (changeId === this.estateChangeId) this.estateId$.next(estateId);
      })
      .catch((error) => {
        if (changeId !== this.estateChangeId) return;
        this.estateReadCancelled$.next();
        this.requestedEstateId = null;
        throw error;
      });
    return this.estateChange;
  }

  public setCompany(companyId: string | null): Promise<void> {
    // Explicit company navigation takes precedence over a pending estate selection.
    ++this.estateChangeId;
    this.estateReadCancelled$.next();
    this.requestedEstateId = null;
    return this.changeCompany(companyId);
  }

  private changeCompany(companyId: string | null): Promise<void> {
    if (companyId === this.requestedCompanyId) return this.companyChange;

    const changeId = ++this.companyChangeId;
    this.requestedCompanyId = companyId;
    this.companyId$.next(null);
    this.localStorageSrv.selectedCompany.set(this.userId$.value ?? '', companyId ?? '');
    this.companyChange = this.dbSrv
      .changeCompany(companyId)
      .then(() => {
        if (changeId === this.companyChangeId) this.companyId$.next(companyId);
      })
      .catch((error) => {
        if (changeId !== this.companyChangeId) return;
        this.requestedCompanyId = null;
        throw error;
      });
    return this.companyChange;
  }

  public setContext(type: ContextType): void {
    if (this.context$.value !== type) {
      if (type === ContextType.COMPANY) {
        ++this.estateChangeId;
        this.estateReadCancelled$.next();
        this.requestedEstateId = null;
      }
      this.context$.next(type);
      this.localStorageSrv.userContext.set(this.userId$.value ?? '', type);
    }
  }

  public getUserId$(): Observable<string | null> {
    return this.userId$.asObservable();
  }

  public getEstateId$(): Observable<string | null> {
    return this.estateId$.asObservable();
  }

  public getCompanyId$(): Observable<string | null> {
    return this.companyId$.asObservable();
  }

  public getContextType$(): Observable<ContextType | null> {
    return this.context$.asObservable();
  }
}
